import { SetStateAction, useCallback, useEffect, useSyncExternalStore } from "react";

function getLocalStorageItem(key: string) {
  return window.localStorage.getItem(key);
}

function removeLocalStorageItem(key: string) {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
}

function setLocalStorageItem(key: string, value: any) {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
}

function dispatchStorageEvent(key: string, newValue: string) {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}

function useLocalStorageSubscribe(callback: () => void) {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}

const getLocalStorageServerSnapshot = () => {
  throw Error("useLocalStorage is a client-only hook");
};

/**
 * Custom hook that uses localStorage as a state management solution.
 * It provides a way to store and retrieve serializable data in localStorage.
 *
 * **Note:** The setter function returned does not have a stable identity like
 * the one returned by `useState`. This means you need to add it to the deps
 * array of your component if you want to use it in a `useEffect` hook, else,
 * it won't use the latest value if you pass a function to it.
 *
 * @param key - The key to use when storing and retrieving data.
 * @param initialValue - The initial value to use when the key is not found in sessionStorage.
 * @returns A tuple containing the current value and a function to update it.
 */
export function useLocalStorage<T>(key: string, initialValue?: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(useLocalStorageSubscribe, getSnapshot, getLocalStorageServerSnapshot);

  const setState = useCallback(
    (v: SetStateAction<T>) => {
      try {
        const nextState = typeof v === "function" ? (v as (prevState: T) => T)(store ? JSON.parse(store) : initialValue) : v;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store]
  );

  useEffect(() => {
    if (getLocalStorageItem(key) === null && typeof initialValue !== "undefined") {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  try {
    const parsedStore = store ? JSON.parse(store) : initialValue;
    return [parsedStore, setState];
  } catch (error) {
    return [(store as T) || initialValue, setState];
  }
}
